import axios from 'axios'
import { BASE_SERVER_URL, sk } from '@/config'
import { Message } from 'element-ui'
import store from '@/store'

const ACCESS_TOKEN_KEY = sk('ACCESS_TOKEN')
const AUTH_ENDPOINTS = ['/public/login', '/public/refresh', '/public/logout']
let refreshPromise = null

export function getAccessToken() {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY)
}

export function persistAuthentication(payload = {}) {
  const token = payload.token
  if (!token) return false

  sessionStorage.setItem(ACCESS_TOKEN_KEY, token)
  // 清除旧版本曾写入 localStorage 的长期访问令牌。
  localStorage.removeItem(sk('ACCESS_TOKEN'))
  localStorage.removeItem(sk('token'))

  const userModel = { ...payload }
  delete userModel.token
  localStorage.setItem(sk('user_model'), JSON.stringify(userModel))
  if (store && store.dispatch) {
    store.dispatch('setToken', token).catch(() => {})
    store.dispatch('setUserInfo', userModel).catch(() => {})
    store.dispatch('permission/setPermissions', userModel.rolePermissionList || []).catch(() => {})
  }
  return true
}

export function clearAuthentication() {
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(sk('ACCESS_TOKEN'))
    localStorage.removeItem(sk('token'))
    localStorage.removeItem(sk('userInfo'))
    localStorage.removeItem(sk('user_model'))
    localStorage.removeItem(sk('uploadInfo'))
    localStorage.removeItem(sk('menus'))
  } catch (e) {}

  if (store && store.dispatch) {
    store.dispatch('permission/clearPermissions').catch(() => {})
    store.dispatch('clearUserData').catch(() => {})
  }
  try {
    const routerModule = require('@/router')
    if (routerModule.resetRouter) routerModule.resetRouter()
  } catch (e) {}
}

function redirectToLogin() {
  try {
    const routerModule = require('@/router')
    const router = routerModule.default
    const currentPath = (router.currentRoute && router.currentRoute.fullPath) || '/'
    if (router.currentRoute.path !== '/login') {
      router.replace({ path: '/login', query: { redirect: currentPath } }).catch(() => {})
    }
  } catch (e) {}
}

function isAuthEndpoint(config = {}) {
  const url = config.url || ''
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))
}

async function requestRefresh() {
  try {
    const response = await axios.post(`${BASE_SERVER_URL}/public/refresh`, {}, {
      withCredentials: true,
      timeout: 120000,
      headers: { VERSION: 'V.1.0.0' }
    })
    const result = response.data || {}
    if (result.code === 200 && persistAuthentication(result.data || {})) {
      return result.data
    }
  } catch (e) {}
  clearAuthentication()
  return null
}

// 全局单例 Promise：同一页面中的并发 401 只会触发一次刷新。
export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = requestRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

const service = axios.create({
  baseURL: BASE_SERVER_URL,
  timeout: 120000,
  withCredentials: true
})

service.interceptors.request.use(
  config => {
    const token = getAccessToken()
    if (token) config.headers['ACCESS-X-TOKEN'] = token
    config.headers.VERSION = 'V.1.0.0'

    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData
    if (!isFormData && !(config.headers && config.headers['Content-Type'])) {
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
    } else if (isFormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  error => Promise.reject(error)
)

async function retryUnauthorized(config) {
  if (!config || config.__authRetried || isAuthEndpoint(config)) {
    clearAuthentication()
    redirectToLogin()
    return null
  }
  config.__authRetried = true
  const session = await refreshSession()
  if (!session) {
    redirectToLogin()
    return null
  }
  config.headers = config.headers || {}
  config.headers['ACCESS-X-TOKEN'] = getAccessToken()
  return service(config)
}

service.interceptors.response.use(
  async response => {
    const res = response.data || {}
    if (res.code === 200 || res.code === 888 || res.code === undefined) return res

    if (res.code === 401) {
      const replay = await retryUnauthorized(response.config)
      return replay || { code: 401, err: res.err || '登录已过期，请重新登录' }
    }

    Message.error({ message: res.err || '操作失败' })
    return res
  },
  async error => {
    if (error.response && error.response.status === 401) {
      const replay = await retryUnauthorized(error.config)
      return replay || { code: 401, err: '登录已过期，请重新登录' }
    }

    let errorMessage = '网络错误'
    if (error && error.message) {
      if (error.message.includes('timeout')) errorMessage = '请求超时，请检查网络连接'
      else if (error.message.includes('Network Error')) errorMessage = '网络连接失败，请检查网络设置'
      else if (error.message.includes('Request failed')) errorMessage = '请求失败，请稍后重试'
      else errorMessage = error.message
    }
    Message.error({ message: errorMessage, duration: 5000 })
    return { code: -1, message: errorMessage }
  }
)

export const request = {
  get(url, params = {}) {
    return service({ method: 'get', url, params })
  },
  post(url, data = {}, config = {}) {
    return service({ method: 'post', url, data, ...config })
  },
  put(url, data = {}) {
    return service({ method: 'put', url, data })
  },
  delete(url, params = {}) {
    return service({ method: 'delete', url, params })
  }
}

export default service
